import { BaseText } from '@/src/components/BaseText'
import { useEnpiBasePath } from '@/src/features/iso-suite/enpi/enpi-paths'
import { useSessionStore } from '@/src/hooks/use_session_store'
import { appColors } from '@/src/theme/foundations/colors'
import { ANALYSES_BASE_ROUTE, ENERGY_COST_BASE_ROUTE, TABS } from '@/src/utils/constants'
import { AskEmberIcon, TrendDownGreen, TrendDownRed, TrendDownYellow, TrendUpGreen, TrendUpRed, TrendUpYellow } from '@assets/icons'
import { Box, Button, Flex, Text } from '@chakra-ui/react'
import { FlexCol, FlexRow } from '@components'
import { useEmberStore } from '@features/Ember/infrastructure/useEmberStore'

import { DEFAULT_BUILDING_NAME, useLocationsContext } from '@/src/contexts/LocationsContext'
import { useBaseToast } from '@/src/hooks/useBaseToast'
import { isProduction, numberFormat } from '@shared/utils'
import { useVirtualizer } from '@tanstack/react-virtual'
import { getDatabase, ref, update } from 'firebase/database'
import moment from 'moment'
import React, { type FC, useEffect, useRef, useState } from 'react'
import { ThumbsDown, ThumbsUp } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { FaRegBuilding } from 'react-icons/fa6'
import { MdOutlineLocationOn } from 'react-icons/md'
import { PiWarningBold } from 'react-icons/pi'
import { useNavigate } from 'react-router-dom'
import { useInsightsStore } from './useInsightsStore'

const getTitleMap = (t: (key: string) => string) => ({
  weekend: t('widgets.insights.weekend_load'),
  consumptionAnomaly: t('widgets.insights.consumption_anomaly'),
  peakload: t('widgets.insights.new_peak_load'),
  efficiency: t('widgets.insights.goal_achievement'),
  emissions: t('widgets.insights.emissions_electricity'),
  budget: t('widgets.insights.budget_status'),
  noPeakload: t('widgets.insights.peak_load_streak'),
})

const reactTreeToText = children => {
  let text = ''
  React.Children.forEach(children, child => {
    if (typeof child === 'string') {
      text += child
    } else if (child?.props?.children) {
      text += reactTreeToText(child.props.children)
    }
  })
  return text
}

const InsightsCardMainContent = ({ insight, valueColor, t }) => {
  return buildText(insight, valueColor, t).html
}

const InsightsCardButtonDescription = ({ insight, t }) => {
  return buildImpact(insight, t).html
}

interface IInsightsCard {
  insight: any
  cardsBgColor: string
  inEmberChat?: boolean
}

export const buildCategory = (insight, t: (key: string) => string) => {
  return insight.isCustom ? insight.category : getTitleMap(t)[insight.type]
}

export const buildKPI = insight => {
  return `${
    insight.isCustom
      ? insight.kpi
      : insight.subtype === 'lowDeviations'
        ? `-${numberFormat(insight.header, 2)}`
        : insight.subtype === 'highDeviations'
          ? `+${numberFormat(insight.header, 2)}`
          : numberFormat(insight.header, 2)
  } ${insight.unitHeader ?? ''}`.trim()
}

export const buildText = (insight, valueColor, t: (key: string, options?: Record<string, unknown>) => string) => {
  const {
    header,
    subtype,
    type,
    unitHeader,
    unitValue,
    value,
    deviationsAmount,
    deviationsConsumption,
    dayName,
    increase,
    monthName,
    text,
    date,
    deviation,
    isCustom,
  } = insight

  const titleMap = getTitleMap(t)
  const contentKey = type

  const insigthTypeContent = {
    consumptionAnomaly:
      subtype === 'lowDeviations' ? (
        <p>
          {t('widgets.insights.consumption_anomaly_less', {
            date,
            deviationsAmount,
            consumption: numberFormat(Math.abs(deviationsConsumption), 2),
            unit: unitValue,
            dayName,
          })}
        </p>
      ) : (
        <p>
          {t('widgets.insights.consumption_anomaly_more', {
            date,
            deviationsAmount,
            consumption: numberFormat(Math.abs(deviationsConsumption), 2),
            unit: unitValue,
            dayName,
          })}
        </p>
      ),
    peakload: (
      <p>
        {t('widgets.insights.peak_load_increased', {
          increase: numberFormat(Math.abs(increase), 2),
          unit: unitValue,
        })}
      </p>
    ),
    efficiency: (
      <p>
        {t('widgets.insights.efficiency_goal_reached', {
          monthName,
          increase: numberFormat(Math.abs(increase), 2),
          unit: unitValue,
        })}
      </p>
    ),
    weekend: (
      <p>
        {t('widgets.insights.weekend_baseload_higher', {
          value: numberFormat(Math.abs(value), 2),
          unit: unitValue,
        })}
      </p>
    ),
    emissions:
      value < 0 ? (
        <p>
          {t('widgets.insights.co2_emissions_lower', {
            value: numberFormat(Math.abs(value), 2),
            unit: unitValue,
          })}
        </p>
      ) : (
        <p>
          {t('widgets.insights.co2_emissions_higher', {
            value: numberFormat(Math.abs(value), 2),
            unit: unitValue,
          })}
        </p>
      ),
    budget:
      subtype === 'inBudgetBand' ? (
        <p>{t('widgets.insights.budget_back_on_track')}</p>
      ) : subtype === 'underBudgetBand' ? (
        <p>
          {t('widgets.insights.budget_under', {
            deviation: numberFormat(Math.abs(deviation), 2),
            unit: unitValue,
          })}
        </p>
      ) : (
        <p>
          {t('widgets.insights.budget_over', {
            deviation: numberFormat(Math.abs(deviation), 2),
            unit: unitValue,
          })}
        </p>
      ),
    noPeakload: (
      <p>
        {t('widgets.insights.peak_load_streak_below', {
          header,
          unitHeader,
          increase: numberFormat(increase, 2),
          unit: unitValue,
        })}
      </p>
    ),
  }

  let ret
  if (isCustom) {
    ret = <p>{text}</p>
  } else if (insigthTypeContent[contentKey] === undefined) {
    ret = `No card content for ${titleMap[contentKey]}`
  } else {
    ret = insigthTypeContent[contentKey]
  }
  const innerText = reactTreeToText(ret.props?.children)

  return { html: ret, text: innerText }
}

export const buildImpact = (insight, t: (key: string, options?: Record<string, unknown>) => string) => {
  const { header, costUnit, costValue, subtype, type, value, impact, efficiencyVariable, isCustom } = insight

  const titleMap = getTitleMap(t)

  const insigthTypeContent = {
    consumptionAnomaly: (
      <span>
        {numberFormat(costValue)} {costUnit}{' '}
        {subtype === 'lowDeviations' ? t('widgets.insights.savings') : t('widgets.insights.additional_costs')}
      </span>
    ),
    peakload: (
      <span>
        {numberFormat(costValue)} {costUnit} {t('widgets.insights.grid_fee_increase')}
      </span>
    ),
    efficiency: (
      <span>{t('widgets.insights.savings_per_unit', { unit: efficiencyVariable?.unit, cost: numberFormat(costValue), costUnit })}</span>
    ),
    weekend: (
      <span>
        {numberFormat(costValue)} {costUnit} {t('widgets.insights.additional_costs')}
      </span>
    ),
    emissions: (
      <span>
        {numberFormat(costValue)} {costUnit} {value < 0 ? t('widgets.insights.savings') : t('widgets.insights.excess_emissions')}
      </span>
    ),
    budget:
      subtype === 'inBudgetBand' ? (
        <span>{t('widgets.insights.on_budget')}</span>
      ) : (
        <span>
          {numberFormat(costValue)} {costUnit}{' '}
          {subtype === 'underBudgetBand' ? t('widgets.insights.under_budget_mtd') : t('widgets.insights.over_budget_mtd')}
        </span>
      ),
    noPeakload: (
      <span>
        {numberFormat(costValue)} {costUnit} {t('widgets.insights.potential')}
      </span>
    ),
  }

  let ret
  if (isCustom) {
    ret = <span>{impact}</span>
  } else if (insigthTypeContent[type] === undefined) {
    ret = `No card content for ${titleMap[type]}`
  } else {
    ret = insigthTypeContent[type]
  }

  const innerText = reactTreeToText(ret.props?.children)
  return { html: ret, text: innerText }
}

export const InsightsCard: FC<IInsightsCard> = ({ insight, cardsBgColor, inEmberChat }) => {
  const { locations, isInitialized } = useLocationsContext()
  const toast = useBaseToast()
  const emberStore = useEmberStore()
  const { t } = useTranslation()
  const enpiBasePath = useEnpiBasePath()

  useEffect(() => {
    if (inEmberChat) return

    const options = {
      root: document.querySelector('#insights-card-scrollable-list'),
      rootMargin: '0px',
      threshold: 1.0,
    }

    const observer = new IntersectionObserver((entries, observer) => {
      const intersectionRatio = entries[0].intersectionRatio
      if (intersectionRatio === 1) {
        const NOW = moment().format('YYYY-MM-DD HH:mm:ss')
        if (isProduction()) {
          setTimeout(() => {
            const rtdb = getDatabase()
            // @ts-ignore
            const insightsRef = ref(rtdb, `insights/${insight.deviceId}/${insight.id}`)
            update(insightsRef, {
              seenAt: NOW,
            })
          }, 10000)
        }
      }
    }, options)

    const target = document.querySelector(`#insight-card-${insight.id}`)
    // @ts-ignore
    observer.observe(target)
  }, [])

  const [insightRelevantCount, setInsightRelevantCount] = useState<number>(insight.relevantCount ?? 0)
  const [isRemoved, setIsRemoved] = useState<boolean>(false)

  const { createdAt, irrelevantCount, relevantCount, subtype, type, value, isCustom, id, buildingName } = insight

  const creationDate = moment(new Date(createdAt)).format('DD.MM.YYYY')

  let valueColor = '#EF4444',
    buttonBackground = '#FEF2F2',
    trendIcon = <TrendUpRed />

  if (type === 'consumptionAnomaly' && subtype === 'lowDeviations') {
    valueColor = '#008236'
    buttonBackground = '#EEFAF3'
    trendIcon = <TrendDownGreen />
  } else if (type === 'efficiency' && subtype === 'efficiencyIncrease') {
    valueColor = '#008236'
    buttonBackground = '#EEFAF3'
    trendIcon = <TrendDownGreen />
  } else if ((type === 'budget' && subtype === 'inBudgetBand') || (type === 'budget' && subtype === 'underBudgetBand')) {
    valueColor = '#008236'
    buttonBackground = '#EEFAF3'
    trendIcon = <TrendDownGreen />
  } else if (type === 'noPeakload' && subtype === 'noPeakloadStreak') {
    valueColor = '#008236'
    buttonBackground = '#EEFAF3'
    trendIcon = <TrendDownGreen />
  } else if (isCustom) {
    if (type === 'positive') {
      if (subtype === 'red') {
        valueColor = '#EF4444'
        buttonBackground = '#FEF2F2'
        trendIcon = <TrendUpRed />
      } else if (subtype === 'green') {
        valueColor = '#008236'
        buttonBackground = '#EEFAF3'
        trendIcon = <TrendUpGreen />
      } else {
        valueColor = '#DA9D00'
        buttonBackground = '#FFF7D9'
        trendIcon = <TrendUpYellow />
      }
    } else if (type === 'negative') {
      if (subtype === 'red') {
        valueColor = '#EF4444'
        buttonBackground = '#FEF2F2'
        trendIcon = <TrendDownRed />
      } else if (subtype === 'green') {
        valueColor = '#008236'
        buttonBackground = '#EEFAF3'
        trendIcon = <TrendDownGreen />
      } else {
        valueColor = '#DA9D00'
        buttonBackground = '#FFF7D9'
        trendIcon = <TrendDownYellow />
      }
    } else if (type === 'attention') {
      if (subtype === 'red') {
        valueColor = '#EF4444'
        buttonBackground = '#FEF2F2'
        trendIcon = (
          <FlexRow borderRadius={'full'} padding='4px' bg='#FEF2F2' borderWidth={'2px'} borderColor={'#EF4444'}>
            <PiWarningBold color='red' />
          </FlexRow>
        )
      } else if (subtype === 'green') {
        valueColor = '#008236'
        buttonBackground = '#EEFAF3'
        trendIcon = (
          <FlexRow borderRadius={'full'} padding='4px' bg='#ECFDF5' borderWidth={'2px'} borderColor={'#008236'}>
            <PiWarningBold color='#008236' />
          </FlexRow>
        )
      } else {
        valueColor = '#DA9D00'
        buttonBackground = '#FFF7D9'
        trendIcon = (
          <FlexRow borderRadius={'full'} padding='4px' bg='#FFF7D9' borderWidth={'2px'} borderColor={'#DA9D00'}>
            <PiWarningBold color='#DA9D00' />
          </FlexRow>
        )
      }
    }
  } else if (type === 'weekend') {
    valueColor = '#EF4444'
    buttonBackground = '#FEF2F2'
    trendIcon = <TrendUpRed />
  } else if (value < 0) {
    valueColor = '#008236'
    buttonBackground = '#EEFAF3'
    trendIcon = <TrendDownGreen />
  }

  const { currentLocation, allLocations, setSelectedBuildingIndex, setSelectedLocationIndex } = useLocationsContext()
  const insightStore = useInsightsStore()
  const session = useSessionStore()
  const navigate = useNavigate()

  const getLocationIndex = (locationId: number) => {
    return allLocations.findIndex(location => location.locationPgId === locationId)
  }

  if (isRemoved) {
    return <></>
  }
  return (
    <FlexCol
      bg={cardsBgColor}
      borderRadius='8px'
      boxShadow='0px 1px 1px 0px rgba(15, 4, 81, 0.13)'
      p={'12px'}
      gap={2}
      border='1px solid transparent'
      _hover={{
        borderWidth: '1px',
        borderColor: '#086AF9',
      }}
      cursor={'pointer'}
      onClick={() => {
        if (
          insight.buildingIndex !== undefined &&
          insight.locationIndex !== undefined &&
          insight.buildingIndex !== null &&
          insight.locationIndex !== null
        ) {
          const locationIndex = getLocationIndex(insight.locationId)
          setSelectedBuildingIndex(insight.buildingIndex)
          setSelectedLocationIndex(locationIndex)
        }
        const NOW = moment().format('YYYY-MM-DD HH:mm:ss')
        const rtdb = getDatabase()
        // @ts-ignore
        const insightsRef = ref(rtdb, `insights/${insight.deviceId}/${insight.id}`)
        update(insightsRef, {
          seenAt: NOW,
        })

        const getRedirectionRoute = (tab?: string) => {
          switch (tab) {
            case TABS.energyInsights.co2Emissions:
              return `${ANALYSES_BASE_ROUTE}/${TABS.analysis.energyInsights}`
            case TABS.energyInsights.energyEfficiency:
              return enpiBasePath
            case TABS.energyInsights.loadProfileDetails:
              return `${ANALYSES_BASE_ROUTE}/${TABS.analysis.lastgangdetails}`
            case TABS.energyInsights.costsBudget:
              return `${ENERGY_COST_BASE_ROUTE}/${TABS.energiekosten.trend}`
            case TABS.energyInsights.consumptionTrends:
              return `${ANALYSES_BASE_ROUTE}/${TABS.analysis.energyInsights}`
            default:
              return `${ANALYSES_BASE_ROUTE}`
          }
        }

        if (insight.isCustom) {
          insightStore.setInsight({ ...insight })
          const energyInsightsTabValues = Object.values(TABS.energyInsights)
          const tab = energyInsightsTabValues[insight.tabIndex] ?? TABS.energyInsights.consumptionTrends
          navigate(getRedirectionRoute(tab))
          return
        }

        insightStore.setInsight({ ...insight })

        switch (type) {
          case 'emissions':
            navigate(getRedirectionRoute(TABS.energyInsights.co2Emissions))
            break
          case 'efficiency':
            navigate(getRedirectionRoute(TABS.energyInsights.energyEfficiency))
            break
          case 'peakload':
          case 'consumptionAnomaly':
          case 'weekend':
            navigate(getRedirectionRoute(TABS.energyInsights.loadProfileDetails))
            break
          case 'budget':
            navigate(getRedirectionRoute(TABS.energyInsights.costsBudget))
            break
          default:
            navigate(getRedirectionRoute())
        }
      }}
    >
      <FlexRow justifyContent='space-between' width='100%' position={'relative'}>
        <Flex gap={2} alignItems={'center'}>
          {trendIcon}

          <Text color='#656565' fontWeight={400} fontSize='14' lineHeight='20px' display='flex'>
            {buildCategory(insight, t)}
          </Text>
        </Flex>

        <Text color='#B4B4B4' fontWeight={400} fontSize='12px' lineHeight='20px' display='flex'>
          {creationDate}
        </Text>

        {insight.seenAt === '' && (
          <Box position={'absolute'} right={'-17px'} top={'-17px'} borderRadius={'full'} w={'12px'} h={'12px'} bg='#EF4444'></Box>
        )}
      </FlexRow>

      <FlexRow justifyContent='space-between' width='100%' mt={1}>
        <Text color='#272727' fontWeight={500} fontSize='16' lineHeight='20px' display='flex'>
          {buildKPI(insight)}
        </Text>
      </FlexRow>

      <FlexRow justifyContent='space-between' width='100%'>
        <Text color='#656565' fontWeight={400} fontSize='14' lineHeight='20px'>
          <InsightsCardMainContent insight={insight} valueColor={valueColor} t={t} />
        </Text>
      </FlexRow>

      <FlexRow
        borderRadius={'8px'}
        justifyContent={'center'}
        mt={1}
        p={'10px 20px'}
        width='100%'
        border={'none'}
        bgColor={buttonBackground}
        color={valueColor}
      >
        <Text fontWeight='normal' fontSize='14'>
          <InsightsCardButtonDescription insight={insight} t={t} />
        </Text>
      </FlexRow>

      {currentLocation?.index === 0 && (
        <FlexRow bg='#EFF3FF' padding='6px 8px 6px 8px' borderRadius={'4px'} alignItems={'center'} justifyContent={'center'}>
          <Box>
            <MdOutlineLocationOn size={16} color='#4B73B0' />
          </Box>
          <Text fontSize={'12px'} color='#4B73B0' mt={'-0.4px'} isTruncated>
            {insight.locationName}
          </Text>
        </FlexRow>
      )}

      {buildingName !== DEFAULT_BUILDING_NAME && (
        <FlexRow bg='#EFF3FF' padding='6px 8px 6px 8px' borderRadius={'4px'} alignItems={'center'} justifyContent={'center'} gap='2px'>
          <Box>
            <FaRegBuilding size={14} color={appColors.secondary} />
          </Box>
          <Text fontSize={'12px'} color='#4B73B0' mt={'-0.4px'} isTruncated>
            {buildingName}
          </Text>
        </FlexRow>
      )}

      {!inEmberChat && (
        <FlexRow alignItems={'center'}>
          <Button
            onClick={e => {
              e.stopPropagation()
              e.preventDefault()
              emberStore.setInsight(insight)
            }}
            p={'0px 2px'}
            size='sm'
            borderWidth={0}
            alignItems={'center'}
            gap='2px'
          >
            <AskEmberIcon />
            <Text color='gray.600' fontSize={'12px'} letterSpacing={'2%'}>
              {t('insights.ask_ember')}
            </Text>
          </Button>
          <FlexRow ml='auto'>
            <Button
              size='sm'
              _hover={{
                backgroundColor: '#ECFDF5',
                border: '1px solid #10B981',
                color: '#10B981',
              }}
              _active={{ backgroundColor: '#358651', color: 'white' }}
              border='0px solid #F9FAFB!important'
              fontSize='14'
              p={'0px 0px'}
              fontWeight='normal'
              color={insightRelevantCount > 0 ? '#10B981' : '#848484'}
              onClick={e => {
                e.stopPropagation()
                if (currentLocation) {
                  const rtdb = getDatabase()

                  const insightsRef = ref(rtdb, `insights/${insight.deviceId}/${id}`)
                  update(insightsRef, {
                    relevantCount: (relevantCount ?? 0) + 1,
                    relevancyActionBy: {
                      name: `${session.user?.firstName} ${session.user?.lastName}`,
                      email: session.user?.email,
                    },
                  })
                  setInsightRelevantCount(insightRelevantCount + 1)
                  toast({
                    description: t('widgets.insights.feedback_thanks'),
                    status: 'success',
                  })
                }
              }}
            >
              <Flex>
                <ThumbsUp size={16} />
                {/* <Text ml={2}>Relevant</Text> */}
              </Flex>
            </Button>

            <Button
              size='sm'
              _hover={{
                backgroundColor: '#FEF2F2',
                border: '1px solid #EF4444',
                color: '#EF4444',
              }}
              _active={{ backgroundColor: '#F40003', color: 'white' }}
              border='0px solid #F9FAFB!important'
              p={'0px 0px'}
              fontSize='14'
              fontWeight={400}
              color='#848484'
              onClick={e => {
                e.stopPropagation()
                if (currentLocation) {
                  const rtdb = getDatabase()
                  const insightsRef = ref(rtdb, `insights/${insight.deviceId}/${id}`)
                  update(insightsRef, {
                    irrelevantCount: irrelevantCount + 1,
                    relevancyActionBy: {
                      name: `${session.user?.firstName} ${session.user?.lastName}`,
                      email: session.user?.email,
                    },
                  })
                  setIsRemoved(true)
                  toast({
                    description: t('widgets.insights.feedback_thanks_removed'),
                    status: 'success',
                  })
                }
              }}
            >
              <Flex>
                <ThumbsDown size={16} />
                {/* <Text ml={2}>Löschen</Text> */}
              </Flex>
            </Button>
          </FlexRow>
        </FlexRow>
      )}
    </FlexCol>
  )
}

interface IInsightsList {
  insights: any[]
  cardsBgColor?: string
  bgColor?: string
  title?: string
  heightOffset?: string
  paddingX?: string
}

const CARD_ESTIMATE_SIZE = 390

export const InsightsList: FC<IInsightsList> = ({ insights, cardsBgColor, bgColor, title, heightOffset, paddingX }) => {
  const parentRef = useRef<HTMLDivElement>(null)

  const rowVirtualizer = useVirtualizer({
    count: insights.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => CARD_ESTIMATE_SIZE,
    overscan: 5,
  })

  const virtualInsights = rowVirtualizer.getVirtualItems()

  return (
    <FlexCol width={'100%'} bgColor={bgColor ?? 'white'} borderRadius='8px' pb={5} paddingX={paddingX} maxW='100%'>
      {title && (
        <BaseText variant='title' mt='24px' ml='24px'>
          {title}
        </BaseText>
      )}

      <Box
        id='insights-card-scrollable-list'
        pt='4px'
        paddingX='8px'
        mt='6px'
        gap='8px'
        overflowY='auto'
        h={heightOffset ? `calc(100vh - ${heightOffset})` : `calc(100vh - 260px)`}
        className='innerScrollBar'
        ref={parentRef}
        onScroll={e => e.stopPropagation()}
      >
        <Box
          style={{
            position: 'relative',
            top: 0,
            left: 0,
            width: '100%',
            transform: `translateY(${virtualInsights[0]?.start ?? 0}px)`,
          }}
        >
          {virtualInsights.map(virtualInsight => {
            const insight = insights[virtualInsight.index]
            return (
              <Box
                data-index={virtualInsight.index}
                ref={rowVirtualizer.measureElement}
                key={insight.id}
                id={`insight-card-${insight.id}`}
                marginBottom={'8px'}
              >
                <InsightsCard insight={insight} cardsBgColor={cardsBgColor ?? ''} />
              </Box>
            )
          })}
        </Box>
      </Box>
    </FlexCol>
  )
}
